import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs, useMediaQuery } from "../../../Styles";
import { CheckBox, CheckStyles, NewInput, NewInputList, NewSelect, NewSelectList } from "../../../Components"
import { ButtonTypes, ListStyle_Types, NewInputHandles, SelectFieldOption_Values } from "../../../Definitions";
import { getBorderRadius } from "../../_Components";
import { DirectionOptions, NumberingOptions, PatternOptions, Pen, useOptionTranslate, StartCornerOptions } from "../../../Definitions/newSectionModalInterfaces";

// TODO
import { useModalContext } from "./_Modals/newSectionModalContext";
import { generateNumberedGrid } from "./_Modals/generateNumberedGrid";
import NewButton from "../../BaseComponents/newButton";

interface CheckedContent {
  feeder: boolean,
  water: boolean,
  activator: boolean,
}

const NewSectionModalLvl1: React.FC = () => {
  const { t } = useTranslation()
  const mobileXs = useMediaQuery(`(max-width: 805px)`);
  const { state: { error, section }, dispatch } = useModalContext()
  const nameRef = React.useRef<NewInputHandles>(null); 

  const {directionOptions, numberingOptions, startCornerOptions, patternOptions} = useOptionTranslate()

  const pens = { ...section, contents: section.contents as Pen[] };

  // Handle pen update when numbering is automatic
  useEffect(() => {
    if (section.numbering === NumberingOptions.automatic && section.startCorner && section.direction && section.pattern && section.startNumber) {
      const autoPens = generateNumberedGrid(
        section.rows,
        section.columns,
        section.startCorner,
        section.direction,
        section.pattern,
        section.startNumber
      );
      dispatch({section:{contents: autoPens}})
    }
  }, [section.numbering, section.rows, section.columns, section.startCorner, section.direction, section.pattern, section.startNumber, dispatch]);

  // Update the start number regardless of whether handleStartNumberChange runs before or after the locations are selected.
  useEffect(() => {
    if (!section.contents.length) return;
    const startNum = Number(section.startNumber ?? 0);
    const updatedPens = section.contents.map((pen, index) => ({
      ...pen,
      name: String(startNum + index),
    }));
  
    dispatch({ section: { contents: updatedPens }});
  }, [section.startNumber]);

  // #region | Handle Input Fields
  const handleRowsChange = (e: any) => {
    dispatch({section:{rows:e}})
    dispatch({section:{contents:[]}})
  };

  const handleColsChange = (e: any) => {
    dispatch({section:{columns:e}})
    dispatch({section:{contents:[]}})
  };

  const handleStartNumberChange = (e: any) => {
    dispatch({section:{startNumber:e}})
  };
  // #endregion

  // #region | Tableview functionality
  const totalCells = section.rows * section.columns;

  const handleCellClick = (penId: number) => {
    const penIdStr = String(penId);
    const existingIndex = pens.contents.findIndex(p => p.penId === penIdStr);
  
    if (existingIndex !== -1) {
      const penToRemove = section.contents[existingIndex];
      const removedNameNum = parseInt(penToRemove.name ?? "", 10);
  
      let updated = pens.contents.filter(p => p.penId !== penIdStr);
  
      updated = updated.map(pen => {
        const nameNum = parseInt(pen.name ?? "", 10);
        if (nameNum > removedNameNum) {
          return { ...pen, name: String(nameNum - 1) };
        }
        return pen;
      });
      dispatch({section:{contents:updated}})
  
      dispatch({section:{numbering:NumberingOptions.manual}})
  
    } else {
      const existingNames = section.contents.map(p => parseInt(p.name ?? "", 10)).filter(n => !isNaN(n));
      const maxName = existingNames.length ? Math.max(...existingNames) : (section.startNumber ?? 0) - 1;
      const newName = maxName + 1;
      const rowStart = Math.floor(penId / section.columns) + 1;
      const colStart = (penId % section.columns) + 1;
      const rowEnd = rowStart + 1;
      const colEnd = colStart + 1;
      const visual = `${rowStart}/${colStart}/${rowEnd}/${colEnd}-${section.rows}/${section.columns}`;
      // const visual = JSON.parse(JSON.stringify(`${rowStart}/${colStart}/${rowEnd}/${colEnd}`));
      const newPen: Pen = { penId: penIdStr, visual, io: null, id: "", name: String(newName) };
      console.log(newName)
      dispatch({section:{contents:[...section.contents, newPen]}})
    }
  };
  
  const getCellNumber = (penId: number) => {
    const index = pens.contents.findIndex(s => s.penId === String(penId));
  
    if (index !== -1) {
      const startNum = Number(section.startNumber ?? 0); 
      const calculatedNumber = startNum + index;
      return String(calculatedNumber);
    }
    return null;
  };
  // #endregion

  // #region | TableView
  const tableView = (
    Array.from({ length: totalCells }).map((_, i) => {
      const isSelected = pens.contents.some(pen => pen.penId === String(i));
      const number = getCellNumber(i);

      const row = Math.floor(i / section.columns);
      const col = i % section.columns;
      const borderRadius = getBorderRadius({ row, col, columns: section.columns, rows: section.rows });

      return (
        <div
          key={i}
          onClick={() => handleCellClick(i)}
          style={{
            textAlign: "center",
            alignContent: "center",
            backgroundColor: colors.greyLightSecondary,
            filter: isSelected ? "opacity(1)" : "opacity(0.5)",
            color: colors.greyDarkPrimary,
            ...fonts.paragraph,
            ...fonts.fontSize4,
            cursor: "pointer",
            borderRadius
          }}
        >
          {number ?? ""}
        </div>
      );
    })
  );
  // #endregion

  // #region | Handle Checkboxes
  const handleCheckboxChange = (name: keyof CheckedContent) => {
    if (name === 'feeder') {
      dispatch({
        section: {
          feeder: 1,
          water: false,
          activator: false,
        },
      });
    } else {
      dispatch({
        section: {
          feeder: 0,
          [name]: !section[name],
        },
      });
    }
  };
  // #endregion
  
  return (
    <div style={{ width: "100%", display: "grid", gridTemplateRows: mobileXs ? "20px 2px 1fr 2px 120px 2px" : "20px 2px 1fr 2px 76px 2px", gap: "5px", gridTemplateColumns: "1fr", height: "100%" }}>
      
      {/* Reset Btn */}
      <div style={{ gridArea: "1/1/2/2", display: "flex", alignItems: "end", justifyContent: "flex-end", padding: "0px 22px", boxSizing: "border-box"}}>
        <NewButton
          content={`${t("reset", {defaultValue: "reset"})}`}
          buttonType={ButtonTypes.textButton}
          endEnhancer={glyphs.ArrowReset({ size: "15px"})}
          onClick={() => dispatch({section:{contents:[]}})}
        />
      </div> 

      <div style={{ gridArea: "2/1/3/2",  borderBottom: "2px solid "+colors.blueDarkSecondary }}/>

      <div style={{gridArea: "3/1/4/2", gap: mobileXs ? "20px" : undefined, padding: "10px", height: "100%", maxHeight: "40vh", boxSizing: "border-box", overflowY: "auto", overflowX: "hidden", scrollbarWidth: "thin", scrollbarColor: colors.blueDarkSecondary+colors.transparent, display: "grid", gridTemplateColumns: mobileXs ? "1fr" : "350px 1fr", gridTemplateRows: mobileXs ? section.numbering === NumberingOptions.automatic ? "460px 1fr": "290px 1fr" : "1fr" }}>
        <div style={{ gridArea: "1/1/2/2", display: "flex", flexDirection: "column", gap: "15px", alignItems: mobileXs ? "center" : undefined }}>  
          {/* Section Name */}
          <NewInput
            ref={nameRef}
            placeholder=""
            header={t("locationFeederPages.sections.sectionName", { defaultValue: "Section Name" })}
            isRequired
            onChange={(e) => dispatch({ section: { name: e } })}
            valueOverrides={{
              defaultValue: section.name
            }}
          />
          {/* Columns, Rows, First Pen No. */}
          <NewInputList
            placeholders={["number", "number", "number"]}
            headers={[t("devices.columns", {defaultValue: "Columns"}), t("devices.rows", {defaultValue: "Rows"}), t("locationFeederPages.sections.firstPenNo", {defaultValue: "First Pen No."})]}
            onChanges={[handleColsChange, handleRowsChange, handleStartNumberChange]}
            valueOverrides={{
              defaultValues: [section.columns, section.rows, section.startNumber ?? 0],
              valueTypes: ["number", "number", "number"],
              maxValueNumbers: [4, undefined, undefined],
              minValueNumbers: [1, 1, 1]
            }}
            listOverrides={{
              listStyle: ListStyle_Types.vertical,
              listGap: "10px"
            }}
          />
          <NewSelect
            header={t("devices.numbering", { defaultValue: "Numbering" })}
            options={numberingOptions}
            valueOverrides={{ defaultValue: numberingOptions.find(opt => opt.value === section.numbering) ?? numberingOptions[0]}}
            onChange={(option: SelectFieldOption_Values | undefined) => {
              if (option && typeof option.id === "number") {
                dispatch({section:{numbering: option.value as NumberingOptions}})
              }
              dispatch({ section: { contents:[] }})
            }}
          />
          {(section.numbering === NumberingOptions.automatic) ? (<>
            <NewSelectList
              placeholders={["", "", ""]}
              headers={[t("devices.startCorner", {defaultValue: "start corner"}), t("devices.direction", {defaultValue: "direction"}), t("devices.pattern", {defaultValue: "pattern"})]}
              options={[startCornerOptions, directionOptions, patternOptions]} 
              listOverrides={{
                listStyle: ListStyle_Types.vertical,
                listGap: "10px"
              }}
              onChanges={[
                (option: SelectFieldOption_Values | undefined) => {
                  if (option && typeof option.id === "number") {
                    dispatch({section:{startCorner: option.value as StartCornerOptions}})
                  }
                },
                (option: SelectFieldOption_Values | undefined) => {
                  if (option && typeof option.id === "number") {
                    dispatch({section:{direction: option.value as DirectionOptions}})
                  }
                },
                (option: SelectFieldOption_Values | undefined) => {
                  if (option && typeof option.id === "number") {
                    dispatch({section:{pattern: option.value as PatternOptions}})
                  }
                }
              ]}
              valueOverrides={{
                defaultValues: [
                  startCornerOptions.find(opt => opt.value === section.startCorner) ?? {id: -1, label: "", value: ""},
                  directionOptions.find(opt => opt.value === section.direction) ?? {id: -1, label: "", value: ""},
                  patternOptions.find(opt => opt.value === section.pattern) ?? {id: -1, label: "", value: ""},
                ]
              }}
            />
          </> ) : ( <></> )}
        </div>

        {/* Table View */}
        <div style={{ gridArea: mobileXs ? "2/1/3/2" : "1/2/2/3", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "start", gap: "5px"}}>
          <div style={{display: "grid", gridTemplateColumns: "4fr 1fr", gap: "5px", position: "relative"}}>         
            <p style={{gridArea: "1/1/2/2", ...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary }}>{`${t("locationFeederPages.sections.penAmount", {defaultValue: "Number of Pens"})}:`}</p>
            <p style={{gridArea: "1/2/2/3", ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, textAlign: "right", lineHeight: "1"}}>{section.contents.length}</p>
            { section.contents.length === 0 ? <div style={{position: "absolute", right: "-25px", top: "-4px"}}><glyphs.Error fillColor={colors.systemRed} /></div> : <></>}
          </div>
          <div style={{
            display: "grid",
            gridTemplateRows: `repeat(${section.rows}, 30px)`,
            gridTemplateColumns: section.columns > 2 ? `repeat(${section.columns}, 50px)` : `repeat(${section.columns}, 80px)`,
            gap: "10px",
          }}>
            {tableView}
            </div>
          <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, textAlign: "center", whiteSpace: 'pre-wrap', maxWidth: "150px"}}>{t("locationFeederPages.sections.clickOnPen", {defaultValue: "Click on a pen to remove it or add it again."})}</p>
        </div>
      </div>

      <div style={{ gridArea: "4/1/5/2", borderBottom: "2px solid "+colors.blueDarkSecondary }}/>

      {/* Pen Content */}
      <div style={{ gridArea: "5/1/6/2", padding: "10px", boxSizing: "border-box", display: "flex", alignItems: "flex-start", justifyContent: "center", flexDirection: "column", gap: "10px", overflow: mobileXs ? "auto" : "hidden", scrollbarWidth: "thin", scrollbarColor: colors.blueDarkSecondary+colors.transparent }}>
        <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 0px 10px" }}>{t("locationFeederPages.sections.penContent", {defaultValue: "Pen Content"})}</p>
        <div style={{width: "fit-content", display: "flex", flexWrap: "wrap", columnGap: mobileXs ? "10px" : "60px", rowGap: "10px", boxSizing: "border-box", margin: "0 auto", justifyContent: "center"}}>
          <CheckBox text={t("locationFeederPages.sections.feederOnly", {defaultValue: "Feeder Only"})} checkStyle={CheckStyles.blue} isChecked={section.feeder !== 0} onChange={() => handleCheckboxChange("feeder")}/>
          <CheckBox text={t("locationFeederPages.sections.water", {defaultValue: "Water"})} checkStyle={CheckStyles.blue}  isChecked={section.water} onChange={() => handleCheckboxChange("water")}/>
          <CheckBox text={t("locationFeederPages.sections.activator", {defaultValue: "Activator"})} checkStyle={CheckStyles.blue}  isChecked={section.activator} onChange={() => handleCheckboxChange("activator")}/>
        </div>
      </div>

      <div style={{ gridArea: "6/1/7/2", borderBottom: "2px solid "+colors.blueDarkSecondary }}/>

    </div>
  );
}

export default NewSectionModalLvl1


